/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useState } from "react";
import { useTheme, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import AddIcon from "@mui/icons-material/Add";
import whiteLogo from "../assets/white-logo.svg";
import blackLogo from "../assets/black-logo.svg";
import { getAuthBaseUrl } from "../utils/authEnvironment";
import { useAuth } from "../context/AuthContext";
import * as teamsApi from "../api/teams";
import { NamePromptDialog } from "./apps/drive/components/DriveDialogs";

// Grouped the way WorkDrive groups its sidebar. `path` is explicit rather than
// derived from the label so nested routes can highlight their parent.
//
// Team Folders is rendered separately below, since it is a live list rather
// than a static entry. Favorites / Labels / Collect Files / Workflows /
// Templates are deliberately absent — nothing backs them yet, and an entry that
// leads nowhere is worse than no entry.
const NAV_SECTIONS = [
  {
    items: [{ label: "Overview", path: "/", Icon: HomeOutlinedIcon }],
  },
  {
    heading: "Files",
    items: [
      { label: "My Folders", path: "/drive", Icon: FolderOutlinedIcon },
      { label: "Recent Files", path: "/recent", Icon: HistoryOutlinedIcon },
      { label: "Favorites", path: "/favorites", Icon: StarBorderOutlinedIcon },
      { label: "Shared with Me", path: "/shared", Icon: PeopleOutlineOutlinedIcon },
      { label: "Trash", path: "/trash", Icon: DeleteOutlineOutlinedIcon },
    ],
  },
];

const Sidebar = () => {
  const theme = useTheme();
  const themeMode = theme.palette.mode;
  const location = useLocation();
  const navigate = useNavigate();
  const { activeOrgId, isOrgAdmin } = useAuth();
  const [teams, setTeams] = useState([]);
  const [creatingTeam, setCreatingTeam] = useState(false);

  // Teams are scoped to the organisation the session is acting as, so this
  // re-runs when the user switches org.
  const loadTeams = useCallback(async () => {
    try {
      setTeams(await teamsApi.listMyTeams());
    } catch {
      setTeams([]);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams, activeOrgId]);

  // TeamPage renaming a team (or anything else that changes the list) has no
  // shared state with this component, so it announces the change instead —
  // otherwise the sidebar keeps showing the old name until the next reload.
  useEffect(() => {
    window.addEventListener("vcs:teams-changed", loadTeams);
    return () => window.removeEventListener("vcs:teams-changed", loadTeams);
  }, [loadTeams]);

  const createTeam = async (teamName) => {
    const team = await teamsApi.createTeam({ teamName });
    await loadTeams();
    navigate(`/teams/${team.id}`);
  };

  // Prefix match so /drive/folders/:id keeps "My Drive" highlighted — the
  // accounts sidebar uses exact equality and loses the highlight on subpages.
  // "/" is exact-only, or it would match every route.
  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <div
      style={{
        position: "relative",
        background: theme.palette.canvas.main,
        boxSizing: "border-box",
        height: "100vh",
        width: "230px",
        flexShrink: 0,
        transition: "0.3s ease all",
        color: theme.palette.text.color,
        zIndex: 20,
      }}
    >
      <Box
        position="absolute"
        right={0}
        top="44px"
        height="calc(100% - 44px)"
        width="0.5px"
        bgcolor={theme.palette.border.color}
      />

      <Box
        sx={{ display: "flex", alignItems: "center", px: 3, gap: 1, cursor: "pointer" }}
        onClick={() => navigate("/drive")}
      >
        <img
          src={themeMode === "dark" ? whiteLogo : blackLogo}
          alt="Vecros"
          style={{ padding: "16px 0px 8px 0px", cursor: "pointer" }}
        />
      </Box>

      <Box sx={{ width: "100%", px: 2, py: 1, boxSizing: "border-box" }}>
        {NAV_SECTIONS.map((section, index) => (
        <Box key={section.heading || index} sx={{ mb: 1.5 }}>
        {section.heading && (
          <Typography
            sx={{
              px: 1.5,
              pt: 1,
              pb: 0.5,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: theme.palette.secondaryText.color,
            }}
          >
            {section.heading}
          </Typography>
        )}
        <ul
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            listStyle: "none",
            padding: 0,
            textAlign: "left",
            margin: "0px",
          }}
        >
          {section.items.map(({ label, path, Icon }) => {
            const active = isActive(path);
            return (
              <Box
                key={path}
                component="li"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  padding: "8px 12px",
                  cursor: "pointer",
                  color: active ? "#FFFFFF" : theme.palette.text.color,
                  textWrap: "nowrap",
                  backgroundColor: active ? "accent.solid" : "transparent",
                  borderRadius: "10px",
                  transition: "0.2s",
                  "&:hover": {
                    backgroundColor: active ? "accent.solid" : theme.palette.tertiary.main,
                  },
                }}
                onClick={() => navigate(path)}
              >
                <Icon
                  sx={{
                    fontSize: 18,
                    color: active ? "#FFFFFF" : theme.palette.secondaryText.color,
                  }}
                />
                <Typography sx={{ fontWeight: 500, fontSize: 13.5 }}>{label}</Typography>
              </Box>
            );
          })}
        </ul>
        </Box>
        ))}

        {/* Team Folders — a live list, and only shown when the session is
            acting as an organisation, since teams belong to one. */}
        {activeOrgId && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", px: 1.5, pt: 1, pb: 0.5 }}>
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: theme.palette.secondaryText.color,
                }}
              >
                Team Folders
              </Typography>
              <Tooltip title="New team">
                <IconButton size="small" onClick={() => setCreatingTeam(true)} aria-label="New team">
                  <AddIcon sx={{ fontSize: 16, color: theme.palette.secondaryText.color }} />
                </IconButton>
              </Tooltip>
            </Box>

            {teams.length === 0 ? (
              <Typography
                sx={{ px: 1.5, py: 0.5, fontSize: 12, color: theme.palette.secondaryText.color }}
              >
                No teams yet.
              </Typography>
            ) : (
              <ul style={{ display: "flex", flexDirection: "column", gap: "4px",
                           listStyle: "none", padding: 0, margin: 0, textAlign: "left" }}>
                {teams.map((team) => {
                  const path = `/teams/${team.id}`;
                  const active = isActive(path);
                  return (
                    <Box
                      key={team.id}
                      component="li"
                      onClick={() => navigate(path)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        padding: "8px 12px",
                        cursor: "pointer",
                        color: active ? "#FFFFFF" : theme.palette.text.color,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        backgroundColor: active ? "accent.solid" : "transparent",
                        borderRadius: "10px",
                        transition: "0.2s",
                        "&:hover": {
                          backgroundColor: active ? "accent.solid" : theme.palette.tertiary.main,
                        },
                      }}
                    >
                      <FolderSharedOutlinedIcon
                        sx={{
                          fontSize: 18,
                          flexShrink: 0,
                          color: active ? "#FFFFFF" : theme.palette.secondaryText.color,
                        }}
                      />
                      <Typography
                        sx={{ fontWeight: 500, fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis" }}
                        title={team.team_name}
                      >
                        {team.team_name}
                      </Typography>
                    </Box>
                  );
                })}
              </ul>
            )}
          </Box>
        )}
      </Box>

      <NamePromptDialog
        open={creatingTeam}
        title="New team"
        label="Team name"
        confirmLabel="Create team"
        onConfirm={createTeam}
        onClose={() => setCreatingTeam(false)}
      />

      <Box sx={{ position: "absolute", bottom: 0, width: "100%", boxSizing: "border-box" }}>
        {/* Pinned at the bottom, like WorkDrive's. Only rendered for org
            admins — the console itself is guarded server-side. */}
        {isOrgAdmin && (
          <Box
            onClick={() => navigate("/admin")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 3,
              py: 1.5,
              cursor: "pointer",
              borderTop: `1px solid ${theme.palette.border.color}`,
              color: theme.palette.text.color,
              "&:hover": { backgroundColor: theme.palette.tertiary.main },
            }}
          >
            <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 500, flex: 1 }}>Admin Console</Typography>
            <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.6 }} />
          </Box>
        )}

        {/* Drive is one service among several; accounts is the hub that owns the
            profile, billing and the rest, so link back rather than duplicating. */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography
            component="a"
            href={getAuthBaseUrl()}
            sx={{
              display: "block",
              px: 1.5,
              py: 1,
              fontSize: 12.5,
              textDecoration: "none",
              borderRadius: "10px",
              color: theme.palette.secondaryText.color,
              "&:hover": { backgroundColor: theme.palette.tertiary.main },
            }}
          >
            ← All Vecros services
          </Typography>
        </Box>
      </Box>
    </div>
  );
};

export default Sidebar;
